import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import { useAppSelector } from "../redux-hooks";
import { deleteProductItem, fetchAllProducts, fetchProductList, fetchProductsByUserRole, fetchProductsIncludingInactiveForProductSearch, getProductDocumentsByProductId, getProductItemByProductItemId, getProductItemBySKU, getProductItemsByProductId, getProductItemsByProductIdForOrder, getProductList, getProductListWithLogos, getProductPricing, productPriceByProductId, getProductShippingConfigForOrder, getAvailableDrugDosabeByProduct } from "../../../service/ProductService";
import { updateElementValue } from "../../../utils/updateElementValue";
import { QuantityOption } from "../../../components/operation/prescription/PrescriptionModalPopup";

export type ProductListInfo = {
    productId : number,
    productName: string,
    hcpcsCode: string,
    inactiveDate: string,
    manufacturer: any,
    productLogo: productLogo,
    productCategory: string
};

interface PaginationProductList {
    content: ProductListInfo[];
    totalElements: number;
    totalPages: number;
    size: number;
  }

export type productLogo = {
    documentId: string,
    documentName: string,
    contentPath: string,
    type: string,
    fileType: string,
    content: string,
}

export type searchParamsInfo = {
    productName: string,
    inactiveDate: string,
}

export type PracticeProductAgreementInfo = {
    productByUserRole: [];
    practiceByUserRole: string;
}

export type ProductPrice = {
    allowableUnitPrice: any,
    macId: string,
    priceType: string,
    productPriceId: string,
    effectiveDate: string,
    inactiveDate: string,
    customPrice: number,
    productItem?: ProductItemInfo,
    tierPrices?: Record<string, number | string>
}

export type ProductInfo = {
    productId: string;
    productName: string
    productPrices: ProductPrice[]
    manufacturerId?: number;
    productStatus?: string;
    manufacturerName?: string;
    //CS-1248 -Start
    productCategory?: any;
    //CS-1248 -End
    //CS-1564
    inactiveDate?: string;
    upfrontPrice?: number | null;
}

export type ProductItemInfo = {
    productItemId: number;
    sku: string;
    size: string;
    units: string;
    inactiveDate: string;
    productItemDescription?:string|null
    hcpcsCode: string;
}

export type ProductItemListInfo = {
    size: number;
    productItemId: number;
    productItems: ProductItemInfo[];
    hcpcsCode: string;
    productItemDescription: string;
}

export type productDocumentListInfo = {
    entityId: string,
    entityName: string,
    type: string,
    description: string
}

export type ProductDosageInfo = {
    dosageId: number;
    productItemId: number;
    externalId: string;
    monthAndDrugName: string;
    directions: string;
    quantity: number;
    refill: number;
    dosageNote: string;
    quantityOptions?: QuantityOption[]
}

export type ProductListState = {
    productName: string,
    inactiveDate: string,
    productList: ProductListInfo[],
    productPriceList: ProductInfo[],
    productPriceListByUserRole: ProductInfo[],
    ProductPriceListForSingleProduct: ProductPrice[],
    manufacturerList: any,
    productItemsInfo: ProductItemListInfo[],
    productDosageList: ProductDosageInfo[],
    status: LoadingType,
    fetchProductsByIdStatus: LoadingType,
    productsByUserRole: ProductInfo[],
    productSearchList?: PaginationProductList | null,
    fetchProductPriceByProductIdStatus: LoadingType,
    // CS-1766 - Product shipping config for order
    productShippingConfigForOrder: any[],
    fetchProductShippingConfigForOrderStatus: LoadingType,
    // CS-1766 - Every active shipping method, used by order fulfillment
    shippingMethodsForFulfillment: any[],
    fetchShippingMethodsForFulfillmentStatus: LoadingType,
    fetchProductDosageListStatus: LoadingType,
};

interface FetchProductItemsParams {
    productId: number;
    onlyActiveRecords: boolean;
}

export type searchParamsProdcutsList = {
    productIds: number[],
    mfgIds: number[],
    status: string[],
    productCategories: string[],
    currentPage: number,
    itemsPerPage: number,
    downloadState?: boolean // CS-1685
}

export type ProductPriceByProductRequest = {
    productId: number,
    effectiveDate: string
}

const initialState = {
    productName: "",
    inactiveDate: "",
    productList: [],
    manufacturerList: [],
    productPriceList: [],
    productPriceListByUserRole: [],
    practiceProductAgreementInfo:[],
    ProductPriceListForSingleProduct: [],
    productDocumentInfo:[],
    productsByUserRole:[],
    productItemsInfo: [],
    productDosageList: [],
    status: "idle" ,
    fetchProductsByIdStatus: "idle",
    fetchProductPriceByProductIdStatus: 'idle',
    productSearchList: undefined,
    // CS-1766 - Product shipping config for order
    productShippingConfigForOrder: [],
    fetchProductShippingConfigForOrderStatus: 'idle',
    // CS-1766 - Every active shipping method, used by order fulfillment
    shippingMethodsForFulfillment: [],
    fetchShippingMethodsForFulfillmentStatus: 'idle',
    fetchProductDosageListStatus: 'idle'
} as ProductListState;

export const getProductsList = createAsyncThunk<
    any,
    { searchParams: searchParamsInfo },
    { rejectValue: string }
>("getProductsList", async (data, { rejectWithValue }) => {
    const { searchParams } = data;
    try {
        const response = await getProductList(searchParams);
        let resp = response.data;
        return resp;
    }
    catch {
        return rejectWithValue("Error while fetching product list")
    }
});

export const getProductListWithLogo = createAsyncThunk("getProductListWithLogo", async () => {
    const response = await getProductListWithLogos();
    let resp = response.data;
    return resp;
})

export const fetchProductPricing = createAsyncThunk("productPricingList", async () => {
    const response = await getProductPricing();
    const v3Response = response.data;
    return v3Response.data;
});

export const deleteProduct = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("getProductList", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await deleteProductItem(productId);
        let resp = response.data;
        return resp;
    }
    catch {
        return rejectWithValue("Error while fetching product list")
    }
});

export const fetchProductsByUserRoleData = createAsyncThunk("fetchProductsByUserRole", async (includeProductPrice: boolean) => {
    const response = await fetchProductsByUserRole(includeProductPrice);
    const v3Response = response.data
    return v3Response.data;
});

export const fetchProductsPriceWithUserRole = createAsyncThunk("fetchAllProducts", async (includeProductPrice: boolean) => {
    const response = await fetchAllProducts(includeProductPrice);
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchProductItemByProductItemId = createAsyncThunk("fetchProductItemByProductItemId", async (productItemId: number) => {
    const response = await getProductItemByProductItemId(productItemId);
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchProductItemBySKU = createAsyncThunk("fetchProductItemBySKU", async (sku: string) => {
    const response = await getProductItemBySKU(sku);
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchProductsList = createAsyncThunk<
    any,
    { searchParams: searchParamsProdcutsList },
    { rejectValue: string }
>("fetchProductsList", async (data, { rejectWithValue }) => {
    const { searchParams } = data;
    try {
        const response = await fetchProductList(searchParams);
        let resp = response.data;
        return resp;
    }
    catch {
        return rejectWithValue("Error while fetching product list")
    }
});

export const fetchProductsForProductsSearch = createAsyncThunk(
    "fetchProductsForProductsSearch",
    async (arg: boolean | { includeProductPrice: boolean; skipLoader?: boolean }) => {
        const includeProductPrice = typeof arg === "boolean" ? arg : !!arg?.includeProductPrice;
        const skipLoader = typeof arg === "boolean" ? false : !!arg?.skipLoader;
        const response = skipLoader
            ? await fetchProductsIncludingInactiveForProductSearch(includeProductPrice, true)
            : await fetchProductsIncludingInactiveForProductSearch(includeProductPrice);
        const v3Response = response.data
        return v3Response.data;
    }
);

export const getProductPriceByProductId = createAsyncThunk('getProductPriceByProductId', async(productPriceByProductRequest :ProductPriceByProductRequest) => {
    const response = await productPriceByProductId(productPriceByProductRequest);
    const v3Response = response.data;
    return v3Response.data;
 });

 export const fetchProductItemsByProductId = createAsyncThunk("fetchProductItemsByProductId", async ({ productId, onlyActiveRecords }: FetchProductItemsParams) => {
    const response = await getProductItemsByProductId(productId, onlyActiveRecords);
    const v3Response = response.data;
    return v3Response.data;
});

 export const fetchProductItemsByProductIdForOrder = createAsyncThunk("fetchProductItemsByProductIdForOrder", async ({ productId, onlyActiveRecords }: FetchProductItemsParams) => {
    const response = await getProductItemsByProductIdForOrder(productId, onlyActiveRecords);
    const v3Response = response.data;
    return v3Response.data;
});

// CS-1766 - Fetch product shipping config for order by product ID
// CS-1997 - practiceId added so free-shipping (P&B) practices get zero-rate, restricted methods.
export const fetchProductShippingConfigForOrder = createAsyncThunk<
    any,
    { productId: number, practiceId?: number },
    { rejectValue: string }
>("fetchProductShippingConfigForOrder", async (data, { rejectWithValue }) => {
    const { productId, practiceId } = data;
    try {
        const response = await getProductShippingConfigForOrder(productId, practiceId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

// CS-1766 - Order fulfillment lists every active shipping method (allMethods = true), so operations
// can ship with any carrier. Kept in its own state slot so the order screen's list never bleeds in.
export const fetchShippingMethodsForFulfillment = createAsyncThunk<
    any,
    { productId: number, practiceId?: number },
    { rejectValue: string }
>("fetchShippingMethodsForFulfillment", async (data, { rejectWithValue }) => {
    const { productId, practiceId } = data;
    try {
        const response = await getProductShippingConfigForOrder(productId, practiceId, true);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

// CS-1766 END

export const fetchAvailableDrugDosabeByProduct = createAsyncThunk<
    any,
    { productId: number },
    { rejectValue: string }
>("fetchAvailableDrugDosabeByProduct", async (data, { rejectWithValue }) => {
    const { productId } = data;
    try {
        const response = await getAvailableDrugDosabeByProduct(productId);
        let resp = response.data;
        return resp;
    }
    catch (error) {
        //@ts-ignore
        return rejectWithValue(error?.data);
    }
});

const productListSlice = createSlice({
    name: "productList",
    initialState,
    reducers: {
        updateElementInProductListSlice : updateElementValue,
        resetProductItems : (state) =>{
            state.productItemsInfo = []
        },
        resetProductsByUserRole : (state) =>{
            state.productsByUserRole = []
            state.fetchProductsByIdStatus = "idle"
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProductsList.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getProductsList.fulfilled, (state, action) => {
                state.status = "success";
                console.log(action.payload)
                state.productList = action.payload?.data;
            })
            .addCase(getProductsList.rejected, (state, action) => {
                state.status = 'error';
            })
            .addCase(getProductListWithLogo.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getProductListWithLogo.fulfilled, (state, action) => {
                state.status = "success";
                console.log(action.payload)
                state.productList = action.payload?.data;
            })
            .addCase(getProductListWithLogo.rejected, (state, action) => {
                state.status = 'error';
            })
            .addCase(deleteProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.status = "success";
                state.productList = action.payload;
            })
            .addCase(deleteProduct.rejected, (state, action) => {
                state.status = 'error';
            })
            .addCase(fetchProductsByUserRoleData.pending, (state) => {
                state.fetchProductsByIdStatus = "loading";
            })
            .addCase(fetchProductsByUserRoleData.fulfilled, (state, action) => {
                state.fetchProductsByIdStatus = "success";
                state.productsByUserRole = action.payload;
            })
            .addCase(fetchProductsByUserRoleData.rejected, (state, action) => {
                state.fetchProductsByIdStatus = 'error';
            })
            .addCase(fetchProductPricing.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProductPricing.fulfilled, (state, action) => {
                state.status = "success";
                state.productPriceList = action.payload;
            })
            .addCase(fetchProductPricing.rejected, (state) => {
                state.status = "error";
            })
            .addCase(fetchProductsPriceWithUserRole.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProductsPriceWithUserRole.fulfilled, (state, action) => {
                state.status = "success";
                state.productPriceListByUserRole = action.payload;
            })
            .addCase(fetchProductsPriceWithUserRole.rejected, (state) => {
                state.status = "error";
            }) 
            .addCase(fetchProductItemsByProductId.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProductItemsByProductId.fulfilled, (state, action) => {
                state.status = "success";
                state.productItemsInfo = action.payload;
            })
            .addCase(fetchProductItemsByProductId.rejected, (state) => {
                state.status = "error";
            })
            .addCase(fetchProductItemsByProductIdForOrder.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProductItemsByProductIdForOrder.fulfilled, (state, action) => {
                state.status = "success";
                state.productItemsInfo = action.payload;
            })
            .addCase(fetchProductItemsByProductIdForOrder.rejected, (state) => {
                state.status = "error";
            })
            .addCase(fetchProductsList.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProductsList.fulfilled, (state, action) => {
                state.status = "success";
                // state.productSearchList = action.payload.data.searchProductData; // CS-1685   // CS-1984: guarded below
                state.productSearchList = action.payload?.data?.searchProductData; // CS-1984
            })
            .addCase(fetchProductsList.rejected, (state) => {
                state.status = "error";
            })
            .addCase(fetchProductsForProductsSearch.pending, (state) => {
                state.fetchProductsByIdStatus = "loading";
            })
            .addCase(fetchProductsForProductsSearch.fulfilled, (state, action) => {
                state.fetchProductsByIdStatus = "success";
                state.productsByUserRole = action.payload;
            })
            .addCase(fetchProductsForProductsSearch.rejected, (state, action) => {
                state.fetchProductsByIdStatus = 'error';
            })
            .addCase(getProductPriceByProductId.pending, (state) => {
                state.fetchProductPriceByProductIdStatus = "loading";
            })
            .addCase(getProductPriceByProductId.fulfilled, (state, action) => {
                state.fetchProductPriceByProductIdStatus = "success";
                state.ProductPriceListForSingleProduct = action.payload;
            })
            .addCase(getProductPriceByProductId.rejected, (state, action) => {
                state.fetchProductPriceByProductIdStatus = 'error';
            })
            // CS-1766 - Product shipping config for order
            .addCase(fetchProductShippingConfigForOrder.pending, (state) => {
                state.fetchProductShippingConfigForOrderStatus = "loading";
            })
            .addCase(fetchProductShippingConfigForOrder.fulfilled, (state, action) => {
                state.fetchProductShippingConfigForOrderStatus = "success";
                state.productShippingConfigForOrder = action.payload?.data;
            })
            .addCase(fetchProductShippingConfigForOrder.rejected, (state) => {
                state.fetchProductShippingConfigForOrderStatus = "error";
            })
            // CS-1766 - Every active shipping method, for order fulfillment
            .addCase(fetchShippingMethodsForFulfillment.pending, (state) => {
                state.fetchShippingMethodsForFulfillmentStatus = "loading";
            })
            .addCase(fetchShippingMethodsForFulfillment.fulfilled, (state, action) => {
                state.fetchShippingMethodsForFulfillmentStatus = "success";
                state.shippingMethodsForFulfillment = action.payload?.data;
            })
            .addCase(fetchShippingMethodsForFulfillment.rejected, (state) => {
                state.fetchShippingMethodsForFulfillmentStatus = "error";
            })
            .addCase(fetchAvailableDrugDosabeByProduct.pending, (state) => {
                state.fetchProductDosageListStatus = "loading";
            })
            .addCase(fetchAvailableDrugDosabeByProduct.fulfilled, (state, action) => {
                state.fetchProductDosageListStatus = "success";
                state.productDosageList = action.payload?.data;
            })
            .addCase(fetchAvailableDrugDosabeByProduct.rejected, (state) => {
                state.fetchProductDosageListStatus = "error";
            })
            // CS-1766 END
    },
  });

export const { resetProductItems, resetProductsByUserRole, updateElementInProductListSlice } = productListSlice.actions;

export const useProductListReducer = () => useAppSelector((state) => state.productListSlice);
export default productListSlice.reducer;